import { FC, useEffect, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import {
    VStack,
    Input,
    Stack,
    Box,
    Textarea,
    Switch,
    Badge,
    useToast,
    Button,
} from "@chakra-ui/react";
import {
    Company,
    CompanyApi,
    User as Entity,
    UserApi as EntityApi,
    School,
    SchoolApi,
    SchoolBatch,
    SchoolBatchApi,
    ServiceApi,
    ServiceGroup,
    ServiceGroupApi,
    Training,
    TrainingApi,
    TrainingYear,
    TrainingYearApi,
    UploadApi,
} from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { AppFormContainer } from "../../../containers";
import {
    AppFormControl,
    AppImageUploader,
    AppMultiSelector,
    AppMultiSelectorOption,
    AppMultiSelectorOptionType,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { APPC, UTIL } from "../../../config";
import { UserDetailView } from "./UserDetailView";

export const UserForm: FC<{
    formContainerState: FormContainerStateReturnType;
    isDetailPage: boolean;
}> = ({ formContainerState, isDetailPage }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { grant } = useAuthData();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);

    // state & const
    const USERTYPE = APPC.BE.User.USERTYPE;
    const dayjs = UTIL.getDayJs();
    const isEditMode: boolean = !!formContainerState.entityId;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Entity>(new Entity());
    const userType: string =
        formContainerState?.entityData?.userType || data?.userType || "";
    const [imageNameFile, setImageNameFile] = useState<File | undefined>(
        undefined
    );
    const [isImageDeleted, setIsImageDeleted] = useState(false);
    const [selectedNationality, setSelectedNationality] =
        useState<AppSelectOption | null>(
            data.nationality
                ? {
                      value: data.nationality,
                      label: data.nationality,
                  }
                : {
                      value: APPC.BE.User.NATIONALITY.NATIONALITY_FRENCH,
                      label: APPC.BE.User.NATIONALITY.NATIONALITY_FRENCH,
                  }
        );
    const [selectedStatus, setSelectedStatus] =
        useState<AppSelectOption | null>(
            data.status
                ? {
                      value: data.status,
                      label: t(`con.User:STUDENT_STATUS_${data.status}`),
                  }
                : {
                      value: APPC.BE.User.STUDENT_STATUS.STUDENT_STATUS_PENDING,
                      label: t(`con.User:STUDENT_STATUS_PENDING`),
                  }
        );
    const [selectedCompanies, setSelectedCompanies] = useState<
        AppMultiSelectorOption[]
    >([]);
    const [selectedSchools, setSelectedSchools] = useState<
        AppMultiSelectorOption[]
    >([]);
    const [loadingStudentTrainers, setLoadingStudentTrainers] = useState(true);
    const [studentTrainerOptions, setStudentTrainerOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedStudentTrainer, setSelectedStudentTrainer] =
        useState<AppSelectOption | null>(null);
    const [loadingSchoolBatch, setLoadingSchoolBatch] = useState(true);
    const [schoolBatchOptions, setSchoolBatchOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedSchoolBatch, setSelectedSchoolBatch] =
        useState<AppSelectOption | null>(null);
    const [loadingService, setLoadingService] = useState(true);
    const [serviceGroupedOptions, setServiceGroupedOptions] = useState<
        { label: string; options: AppSelectOption[] }[]
    >([]);
    const [selectedServices, setSelectedServices] = useState<AppSelectOption[]>(
        []
    );
    const [loadingTraining, setLoadingTraining] = useState(true);
    const [trainingOptions, setTrainingOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTraining, setSelectedTraining] =
        useState<AppSelectOption | null>(null);
    const [loadingTrainingYear, setLoadingTrainingYear] = useState(true);
    const [trainingYearOptions, setTrainingYearOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedTrainingYear, setSelectedTrainingYear] =
        useState<AppSelectOption | null>(null);

    const {
        handleSubmit,
        register,
        reset,
        control,
        formState: { errors },
        setValue,
    } = useForm<Entity>({
        resolver: yupResolver(
            schema(t, isEditMode, userType === USERTYPE.USERTYPE_STUDENT)
        ),
        mode: "onSubmit",
    });

    // handler
    const onSubmitHandler = async (formData: Entity) => {
        setLoading(true);
        if (imageNameFile) {
            const uploadFormData = new FormData();
            uploadFormData.set(
                "fileType",
                APPC.BE.Upload.FILETYPE.FILETYPE_USER_AVATAR
            );
            uploadFormData.set("file", imageNameFile);

            await UploadApi.upload<{ fileName: string }, FormData>(
                uploadFormData
            ).then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    formData.imageName = response.fileName;
                }
            });
        }
        if (isImageDeleted) {
            formData.imageName = "";
        }
        formData.userType = userType;
        formData.dob = formData.dob ? formData.dob : null;
        if (UTIL.isSchoolCompanyMultiSelect(userType)) {
            formData.companies = selectedCompanies?.map((item) => item.value);
            formData.schools = selectedSchools?.map((item) => item.value);
        } else if (!isEditMode && activeSchoolOrCompany) {
            if (UTIL.isSchoolType(userType)) {
                formData.schools = [activeSchoolOrCompany?.["@id"]];
            }
            if (UTIL.isCompanyType(userType)) {
                formData.companies = [activeSchoolOrCompany?.["@id"]];
            }
        }
        EntityApi.createOrUpdate<Entity, Entity>(
            formContainerState.entityId,
            formData
        )
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    const loadCompanyOptions = async (
        page: number,
        search: string,
        pageSize: number
    ): Promise<AppMultiSelectorOptionType> => {
        const params = {
            itemsPerPage: pageSize,
            [`order[name]`]: "asc",
            pagination: true,
        };

        if (search) {
            params["company_search"] = search;
        }

        let apiCall = "getCollection";
        if (grant.isInternshipCoordinator || grant.isSchoolAndCompany) {
            apiCall = "getCollectionMyCompanies";
        }

        const { response } = await CompanyApi[apiCall]<Company>(page, params);

        if (response !== null) {
            const newOptions =
                response.items?.map((e) => ({
                    value: CompanyApi.toResourceIRI(e.id),
                    label: `${e.id} ${e.name}`,
                })) || [];
            return {
                data: newOptions,
                totalItems: response.totalItems,
            };
        }

        return { data: [], totalItems: 0 };
    };

    const loadSchoolOptions = async (
        page: number,
        search: string,
        pageSize: number
    ): Promise<AppMultiSelectorOptionType> => {
        const params = {
            itemsPerPage: pageSize,
            [`order[name]`]: "asc",
            pagination: true,
        };

        if (search) {
            params["school_search"] = search;
        }

        let apiCall = "getCollection";
        if (grant.isInternshipCoordinatorTrainer || grant.isSchoolAndCompany) {
            apiCall = "getCollectionMySchools";
        }

        const { response } = await SchoolApi[apiCall]<School>(page, params);

        if (response !== null) {
            const newOptions =
                response.items?.map((e) => ({
                    value: SchoolApi.toResourceIRI(e.id),
                    label: `${e.id} ${e.name}`,
                })) || [];
            return {
                data: newOptions,
                totalItems: response.totalItems,
            };
        }

        return { data: [], totalItems: 0 };
    };

    const loadUserOptions = () => {
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
            userType: USERTYPE.USERTYPE_PEDAGOGICAL_REFERENCE_TRAINER,
        };
        if (activeSchoolOrCompany?.["@type"] === "School") {
            params["schools.id"] = activeSchoolOrCompany?.id;
        }
        setLoadingStudentTrainers(true);
        EntityApi.getCollection<Entity>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setStudentTrainerOptions(
                        response?.items?.map((e) => ({
                            value: EntityApi.toResourceIRI(e.id),
                            label: e.fullName,
                        })) || []
                    );
                }
            })
            .finally(() => setLoadingStudentTrainers(false));
    };

    const loadSchoolBatchOptions = () => {
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
        };
        if (activeSchoolOrCompany?.["@type"] === "School") {
            params["school.id"] = activeSchoolOrCompany?.id;
        }
        setLoadingSchoolBatch(true);
        SchoolBatchApi.getCollection<SchoolBatch>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setSchoolBatchOptions(
                        response?.items?.map((e) => ({
                            value: SchoolBatchApi.toResourceIRI(e.id),
                            label: e.name,
                        })) || []
                    );
                }
            })
            .finally(() => setLoadingSchoolBatch(false));
    };

    const loadServiceOptions = () => {
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
        };
        if (activeSchoolOrCompany?.["@type"] === "Company") {
            params["company.id"] = activeSchoolOrCompany?.id;
        }
        setLoadingService(true);
        ServiceGroupApi.getCollection<ServiceGroup>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    const options = response.items.map((group) => ({
                        label: group.name,
                        options: (group.services || []).map((service) => ({
                            value: ServiceApi.toResourceIRI(service.id),
                            label: service.name,
                        })),
                    }));
                    setServiceGroupedOptions(options);
                }
            })
            .finally(() => setLoadingService(false));
    };

    const loadTrainingOptions = () => {
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
        };
        setLoadingTraining(true);
        TrainingApi.getCollectionForSelect<Training>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setTrainingOptions(
                        response?.items?.map((e) => ({
                            value: TrainingApi.toResourceIRI(e.id),
                            label: e.name,
                        })) || []
                    );
                }
            })
            .finally(() => setLoadingTraining(false));
    };

    const loadTrainingYearOptions = () => {
        if (!selectedTraining) {
            return;
        }
        const params = {
            [`order[code]`]: "asc",
            "training.id": `${selectedTraining?.value}`,
            pagination: false,
        };
        setLoadingTrainingYear(true);
        TrainingYearApi.getCollectionForSelect<TrainingYear>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setTrainingYearOptions(
                        response?.items?.map((e) => ({
                            value: TrainingYearApi.toResourceIRI(e.id),
                            label: `${e.code} - ${e.year}`,
                        })) || []
                    );
                }
            })
            .finally(() => setLoadingTrainingYear(false));
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.entityId) {
            reset();
            setData(new Entity());
            return;
        }
        setLoading(true);
        EntityApi.getItem<Entity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    if (UTIL.isSchoolCompanyMultiSelect(response?.userType)) {
                        setSelectedSchools(
                            (response.schools as School[]).map((item) => ({
                                value: SchoolApi.toResourceIRI(item.id),
                                label: item.name,
                            }))
                        );
                        setSelectedCompanies(
                            (response.companies as Company[]).map((item) => ({
                                value: CompanyApi.toResourceIRI(item.id),
                                label: item.name,
                            }))
                        );
                    }
                    if (
                        response.userType ===
                            USERTYPE.USERTYPE_INTERNSHIP_SUPERVISOR &&
                        response.companyServices
                    ) {
                        setSelectedServices(
                            response.companyServices.map((service) => ({
                                value: ServiceApi.toResourceIRI(service.id),
                                label: service.name,
                            }))
                        );
                    }
                    if (
                        response.userType === USERTYPE.USERTYPE_STUDENT &&
                        response.studentTrainer &&
                        typeof response.studentTrainer !== "string"
                    ) {
                        setSelectedStudentTrainer({
                            value: EntityApi.toResourceIRI(
                                response.studentTrainer.id
                            ),
                            label: response.studentTrainer.fullName,
                        });
                    }
                    if (
                        response.userType === USERTYPE.USERTYPE_STUDENT &&
                        response.schoolBatch &&
                        typeof response.schoolBatch !== "string"
                    ) {
                        setSelectedSchoolBatch({
                            value: SchoolBatchApi.toResourceIRI(
                                response.schoolBatch.id
                            ),
                            label: response.schoolBatch.name,
                        });
                    }
                    if (
                        response.userType === USERTYPE.USERTYPE_STUDENT &&
                        response.training &&
                        typeof response.training !== "string"
                    ) {
                        setSelectedTraining({
                            value: TrainingApi.toResourceIRI(
                                response.training.id
                            ),
                            label: response.training.name,
                        });
                    }
                    if (
                        response.userType === USERTYPE.USERTYPE_STUDENT &&
                        response.trainingYear &&
                        typeof response.trainingYear !== "string"
                    ) {
                        setSelectedTrainingYear({
                            value: TrainingYearApi.toResourceIRI(
                                response.trainingYear.id
                            ),
                            label: `${response.trainingYear.code} - ${response.trainingYear.year}`,
                        });
                    }
                    if (response.nationality) {
                        setSelectedNationality({
                            value: response.nationality,
                            label: response.nationality,
                        });
                    }
                    if (response.status) {
                        setSelectedStatus({
                            value: response.status,
                            label: t(
                                `con.User:STUDENT_STATUS_${response.status}`
                            ),
                        });
                    }
                    setData(response);
                }
            })
            .finally(() => setLoading(false));
    }, [formContainerState.entityId]);

    useEffect(() => {
        if (userType === USERTYPE.USERTYPE_STUDENT) {
            loadUserOptions();
            loadSchoolBatchOptions();
            loadTrainingOptions();
        }
        if (userType === USERTYPE.USERTYPE_INTERNSHIP_SUPERVISOR) {
            loadServiceOptions();
        }
    }, [userType]);

    useEffect(() => {
        if (userType === USERTYPE.USERTYPE_STUDENT) {
            loadTrainingYearOptions();
        }
    }, [userType, selectedTraining]);

    // render
    const renderForm = () => {
        return (
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"}>
                    <AppFormControl
                        label={t("ent.User:imageName.label")}
                        isLoading={loading}
                    >
                        <AppImageUploader
                            fileType={
                                APPC.BE.Upload.FILETYPE.FILETYPE_USER_AVATAR
                            }
                            fileName={data?.imageName}
                            onDone={(file) => {
                                if (!file) {
                                    setIsImageDeleted(true);
                                }
                                setImageNameFile(file);
                            }}
                            isAvatar
                        />
                    </AppFormControl>
                    <Badge variant={"secondary"} alignSelf={"flex-start"}>
                        {t(`con.User:USERTYPE_${userType}`)}
                    </Badge>
                    <AppFormControl
                        label={t("ent.User:email.label")}
                        isInvalid={!!errors?.email}
                        message={errors?.email?.message}
                        isLoading={loading}
                    >
                        <Input
                            type="email"
                            placeholder={t("ent.User:email.placeholder")}
                            {...register("email")}
                            defaultValue={data.email}
                            disabled={isEditMode}
                        />
                    </AppFormControl>
                    <Stack
                        gap={5}
                        direction={{
                            base: "column",
                            sm: "column",
                            md: "row",
                        }}
                    >
                        <AppFormControl
                            label={t("ent.User:firstName.label")}
                            isInvalid={!!errors?.firstName}
                            message={errors?.firstName?.message}
                            isLoading={loading}
                        >
                            <Input
                                type="text"
                                placeholder={t(
                                    "ent.User:firstName.placeholder"
                                )}
                                {...register("firstName")}
                                defaultValue={data.firstName}
                            />
                        </AppFormControl>
                        <AppFormControl
                            label={t("ent.User:lastName.label")}
                            isInvalid={!!errors?.lastName}
                            message={errors?.lastName?.message}
                            isLoading={loading}
                        >
                            <Input
                                type="text"
                                placeholder={t("ent.User:lastName.placeholder")}
                                {...register("lastName")}
                                defaultValue={data.lastName}
                            />
                        </AppFormControl>
                    </Stack>
                    <Stack
                        gap={5}
                        direction={{
                            base: "column",
                            sm: "column",
                            md: "row",
                        }}
                    >
                        <AppFormControl
                            label={t("ent.User:phone.label")}
                            isLoading={loading}
                        >
                            <Input
                                type="text"
                                placeholder={t("ent.User:phone.placeholder")}
                                {...register("phone")}
                                defaultValue={data.phone}
                            />
                        </AppFormControl>
                        {userType === USERTYPE.USERTYPE_STUDENT ? (
                            <AppFormControl
                                label={t("ent.User:studentTrainer.label")}
                                isLoading={loading}
                            >
                                <Controller
                                    control={control}
                                    name="studentTrainer"
                                    render={({ field }) => (
                                        <AppSelect<AppSelectOption>
                                            name={field.name}
                                            isClearable
                                            isLoading={loadingStudentTrainers}
                                            options={studentTrainerOptions}
                                            value={
                                                selectedStudentTrainer || null
                                            }
                                            placeholder={t(
                                                "ent.User:studentTrainer.placeholder"
                                            )}
                                            onChange={(e) => {
                                                field.onChange(
                                                    e?.value || null
                                                );
                                                setSelectedStudentTrainer(
                                                    e as AppSelectOption
                                                );
                                            }}
                                        />
                                    )}
                                />
                            </AppFormControl>
                        ) : userType ===
                          USERTYPE.USERTYPE_INTERNSHIP_SUPERVISOR ? (
                            <AppFormControl
                                label={t("ent.User:companyServices.label")}
                                isLoading={loading}
                            >
                                <Controller
                                    control={control}
                                    name="companyServices"
                                    render={({ field }) => (
                                        <AppSelect<AppSelectOption, true>
                                            name={field.name}
                                            isClearable
                                            isMulti={true}
                                            isLoading={loadingService}
                                            options={serviceGroupedOptions}
                                            value={selectedServices || null}
                                            placeholder={t(
                                                "ent.User:companyServices.placeholder"
                                            )}
                                            onChange={(e) => {
                                                field.onChange(
                                                    e?.map(
                                                        (option) =>
                                                            option?.value
                                                    ) || []
                                                );
                                                setSelectedServices(
                                                    e as AppSelectOption[]
                                                );
                                            }}
                                        />
                                    )}
                                />
                            </AppFormControl>
                        ) : (
                            <AppFormControl>
                                <></>
                            </AppFormControl>
                        )}
                    </Stack>
                    {UTIL.isSchoolCompanyMultiSelect(userType) && (
                        <Stack
                            gap={5}
                            direction={{
                                base: "column",
                                sm: "column",
                                md: "row",
                            }}
                        >
                            {UTIL.isSchoolType(userType) && (
                                <AppFormControl
                                    label={t("ent.User:schools.label")}
                                    isLoading={loading}
                                >
                                    <AppMultiSelector
                                        onSelect={(data) =>
                                            setSelectedSchools(data)
                                        }
                                        loadOptions={loadSchoolOptions}
                                        selectedOpt={selectedSchools}
                                        subject={t("ent.User:schools.label")}
                                        triggerElement={
                                            <Button
                                                size={"sm"}
                                                variant={"secondary"}
                                            >
                                                {t(
                                                    "padm.UserForm:button.selectSchool"
                                                )}
                                            </Button>
                                        }
                                        isModal={true}
                                        isSearch={true}
                                        selectorRows={8}
                                        maxSelectOptions={
                                            UTIL.isSchoolCompanyMultiSelect(
                                                userType
                                            )
                                                ? undefined
                                                : 1
                                        }
                                    />
                                </AppFormControl>
                            )}
                            {UTIL.isCompanyType(userType) && (
                                <AppFormControl
                                    label={t("ent.User:companies.label")}
                                    isLoading={loading}
                                >
                                    <AppMultiSelector
                                        onSelect={(data) =>
                                            setSelectedCompanies(data)
                                        }
                                        loadOptions={loadCompanyOptions}
                                        selectedOpt={selectedCompanies}
                                        subject={t("ent.User:companies.label")}
                                        triggerElement={
                                            <Button
                                                size={"sm"}
                                                variant={"secondary"}
                                            >
                                                {t(
                                                    "padm.UserForm:button.selectCompany"
                                                )}
                                            </Button>
                                        }
                                        isModal={true}
                                        isSearch={true}
                                        selectorRows={8}
                                        maxSelectOptions={
                                            UTIL.isSchoolCompanyMultiSelect(
                                                userType
                                            )
                                                ? undefined
                                                : 1
                                        }
                                    />
                                </AppFormControl>
                            )}
                        </Stack>
                    )}
                    {userType === USERTYPE.USERTYPE_STUDENT && (
                        <>
                            <Stack
                                gap={5}
                                direction={{
                                    base: "column",
                                    sm: "column",
                                    md: "row",
                                }}
                            >
                                <AppFormControl
                                    label={t("ent.User:dob.label")}
                                    isLoading={loading}
                                >
                                    <Input
                                        type="date"
                                        placeholder={t(
                                            "ent.User:dob.placeholder"
                                        )}
                                        {...register("dob")}
                                        defaultValue={
                                            data.dob
                                                ? dayjs(data?.dob).format(
                                                      APPC.APP_HTML_DATE_FORMAT
                                                  )
                                                : undefined
                                        }
                                    />
                                </AppFormControl>
                                <AppFormControl
                                    label={t("ent.User:nationality.label")}
                                    isLoading={loading}
                                >
                                    <Controller
                                        control={control}
                                        name="nationality"
                                        defaultValue={
                                            data.nationality ||
                                            APPC.BE.User.NATIONALITY
                                                .NATIONALITY_FRENCH
                                        }
                                        render={({ field }) => (
                                            <AppSelect<AppSelectOption>
                                                name={field.name}
                                                isClearable
                                                isLoading={loading}
                                                options={Object.values(
                                                    APPC.BE.User.NATIONALITY
                                                ).map((nat) => ({
                                                    value: nat,
                                                    label: nat,
                                                }))}
                                                value={selectedNationality}
                                                placeholder={t(
                                                    "ent.User:nationality.placeholder"
                                                )}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e?.value || null
                                                    );
                                                    setSelectedNationality(
                                                        e as AppSelectOption
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </AppFormControl>
                            </Stack>
                            <Stack
                                gap={5}
                                direction={{
                                    base: "column",
                                    sm: "column",
                                    md: "row",
                                }}
                            >
                                <AppFormControl
                                    label={t("ent.User:schoolBatch.label")}
                                    isLoading={loading}
                                >
                                    <Controller
                                        control={control}
                                        name="schoolBatch"
                                        render={({ field }) => (
                                            <AppSelect<AppSelectOption>
                                                name={field.name}
                                                isClearable
                                                isLoading={loadingSchoolBatch}
                                                options={schoolBatchOptions}
                                                value={
                                                    selectedSchoolBatch || null
                                                }
                                                placeholder={t(
                                                    "ent.User:schoolBatch.placeholder"
                                                )}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e?.value || null
                                                    );
                                                    setSelectedSchoolBatch(
                                                        e as AppSelectOption
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </AppFormControl>
                                <AppFormControl
                                    label={t("ent.User:status.label")}
                                    isLoading={loading}
                                >
                                    <Controller
                                        control={control}
                                        name="status"
                                        defaultValue={
                                            data.status ||
                                            APPC.BE.User.STUDENT_STATUS
                                                .STUDENT_STATUS_PENDING
                                        }
                                        render={({ field }) => (
                                            <AppSelect<AppSelectOption>
                                                name={field.name}
                                                isClearable
                                                isLoading={loading}
                                                options={Object.values(
                                                    APPC.BE.User.STUDENT_STATUS
                                                ).map((s) => ({
                                                    value: s,
                                                    label: t(
                                                        `con.User:STUDENT_STATUS_${s}`
                                                    ),
                                                }))}
                                                value={selectedStatus}
                                                placeholder={t(
                                                    "ent.User:status.placeholder"
                                                )}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e?.value || null
                                                    );
                                                    setSelectedStatus(
                                                        e as AppSelectOption
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </AppFormControl>
                            </Stack>
                            <Stack
                                gap={5}
                                direction={{
                                    base: "column",
                                    sm: "column",
                                    md: "row",
                                }}
                            >
                                <AppFormControl
                                    label={t("ent.User:training.label")}
                                    isInvalid={!!errors?.training}
                                    message={errors?.training?.message}
                                    isLoading={loading}
                                >
                                    <Controller
                                        control={control}
                                        name="training"
                                        defaultValue={
                                            (data.training as Training)?.["@id"]
                                        }
                                        render={({ field }) => (
                                            <AppSelect<AppSelectOption>
                                                name={field.name}
                                                isClearable
                                                isLoading={loadingTraining}
                                                options={trainingOptions}
                                                value={selectedTraining || null}
                                                placeholder={t(
                                                    "ent.User:training.placeholder"
                                                )}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e?.value || null
                                                    );
                                                    setValue(
                                                        "trainingYear",
                                                        undefined
                                                    );
                                                    setSelectedTraining(
                                                        e as AppSelectOption
                                                    );
                                                    setSelectedTrainingYear(
                                                        null
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </AppFormControl>
                                <AppFormControl
                                    label={t("ent.User:trainingYear.label")}
                                    isInvalid={!!errors?.trainingYear}
                                    message={errors?.trainingYear?.message}
                                    isLoading={loading}
                                >
                                    <Controller
                                        control={control}
                                        name="trainingYear"
                                        defaultValue={
                                            (
                                                data.trainingYear as TrainingYear
                                            )?.["@id"]
                                        }
                                        render={({ field }) => (
                                            <AppSelect<AppSelectOption>
                                                name={field.name}
                                                isClearable
                                                isLoading={loadingTrainingYear}
                                                options={trainingYearOptions}
                                                value={
                                                    selectedTrainingYear || null
                                                }
                                                placeholder={t(
                                                    "ent.User:trainingYear.placeholder"
                                                )}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e?.value || null
                                                    );
                                                    setSelectedTrainingYear(
                                                        e as AppSelectOption
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </AppFormControl>
                            </Stack>
                            <Stack
                                w={"full"}
                                gap={5}
                                direction={{
                                    base: "column",
                                    sm: "column",
                                    md: "row",
                                }}
                            >
                                <Box
                                    w={{
                                        base: "full",
                                        sm: "full",
                                        md: "50%",
                                    }}
                                >
                                    <AppFormControl
                                        label={t("ent.User:address.label")}
                                        isLoading={loading}
                                    >
                                        <Textarea
                                            h="131px"
                                            resize={"none"}
                                            placeholder={t(
                                                "ent.User:address.placeholder"
                                            )}
                                            {...register("address")}
                                            defaultValue={data.address}
                                        />
                                    </AppFormControl>
                                </Box>
                                <Box
                                    w={{
                                        base: "full",
                                        sm: "full",
                                        md: "50%",
                                    }}
                                >
                                    <VStack
                                        justifyContent={"space-between"}
                                        gap={5}
                                    >
                                        <AppFormControl
                                            label={t("ent.User:zipCode.label")}
                                            isInvalid={!!errors.zipCode}
                                            message={errors.zipCode?.message}
                                            isLoading={loading}
                                        >
                                            <Input
                                                type="text"
                                                placeholder={t(
                                                    "ent.User:zipCode.placeholder"
                                                )}
                                                {...register("zipCode")}
                                                defaultValue={data.zipCode}
                                            />
                                        </AppFormControl>
                                        <AppFormControl
                                            label={t("ent.User:city.label")}
                                            isLoading={loading}
                                        >
                                            <Input
                                                type="text"
                                                placeholder={t(
                                                    "ent.User:city.placeholder"
                                                )}
                                                {...register("city")}
                                                defaultValue={data.city}
                                            />
                                        </AppFormControl>
                                    </VStack>
                                </Box>
                            </Stack>
                        </>
                    )}
                    <AppFormControl
                        label={t("ent.User:isActive.label")}
                        isInvalid={!!errors.isActive}
                        message={errors.isActive?.message}
                        isLoading={loading}
                    >
                        <Switch
                            {...register("isActive")}
                            defaultChecked={isEditMode ? !!data.isActive : true}
                        />
                    </AppFormControl>
                </VStack>
            </form>
        );
    };

    const renderDetail = () => {
        return <UserDetailView data={data} />;
    };

    return (
        <AppFormContainer
            title={t("padm.UserForm:text.pageTitle")}
            state={formContainerState}
            type={APPC.FORM_CONTAINER}
            size={"xl"}
            isFooter={!isDetailPage}
        >
            {isDetailPage ? renderDetail() : renderForm()}
        </AppFormContainer>
    );
};
